import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiMessageCircle } from 'react-icons/fi';
import ChatButton from '../../components/Chat/ChatButton';
import './AdminVendors.css';

const AdminVendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(null);

  useEffect(() => {
    fetchVendors();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = () => {
    const adminToken = localStorage.getItem('adminToken');
    const userToken = localStorage.getItem('token');
    const storedToken = adminToken || userToken;
    
    setToken(storedToken);
    
    // Try to get user info from localStorage or decode token
    const userInfo = localStorage.getItem('user');
    if (userInfo) {
      setCurrentUser(JSON.parse(userInfo));
    }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('http://localhost:5001/api/v1/admin/vendors', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to load vendors');
      setVendors([]); // Ensure vendors is always an array even on error
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(
        `http://localhost:5001/api/v1/admin/vendors/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vendor status updated');
      fetchVendors();
    } catch (error) {
      console.error('Error updating vendor:', error);
      toast.error('Failed to update vendor');
    }
  };

  const filteredVendors = vendors.filter(vendor => {
    const matchesFilter = filter === 'all' || vendor.status === filter;
    const matchesSearch =
      vendor.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      Active: 'badge-success',
      Inactive: 'badge-danger',
      Pending: 'badge-warning',
      Suspended: 'badge-secondary'
    };
    return statusMap[status] || 'badge-secondary';
  };

  if (loading) {
    return <div className="admin-vendors"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="admin-vendors">
      <div className="page-header">
        {/* <h1>Vendors</h1> */}
        <h1>Tenants</h1>
        <div className="header-stats">
          <div className="stat-card">
            <span className="stat-label">Total</span>
            <span className="stat-value">{vendors.length}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Active</span>
            <span className="stat-value">{vendors.filter(v => v.status === 'Active').length}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Pending</span>
            <span className="stat-value">{vendors.filter(v => v.status === 'Pending').length}</span>
          </div>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, category, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-buttons">
          <button 
            className={filter === 'all' ? 'active' : ''} 
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button 
            className={filter === 'Active' ? 'active' : ''} 
            onClick={() => setFilter('Active')}
          >
            Active
          </button>
          <button 
            className={filter === 'Pending' ? 'active' : ''} 
            onClick={() => setFilter('Pending')}
          >
            Pending
          </button>
          <button 
            className={filter === 'Inactive' ? 'active' : ''} 
            onClick={() => setFilter('Inactive')}
          >
            Inactive
          </button>
          <button 
            className={filter === 'Suspended' ? 'active' : ''} 
            onClick={() => setFilter('Suspended')}
          >
            Suspended
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="vendors-table">
          <thead>
            <tr>
              <th>Company Name</th>
              <th>Vendor Code</th>
              <th>Category</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Rating</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredVendors.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">No vendors found</td>
              </tr>
            ) : (
              filteredVendors.map(vendor => (
                <tr key={vendor.id}>
                  <td><strong>{vendor.company_name}</strong></td>
                  <td>{vendor.vendor_code || 'N/A'}</td>
                  <td>{vendor.category || 'N/A'}</td>
                  <td>{vendor.email || 'N/A'}</td>
                  <td>{vendor.phone || 'N/A'}</td>
                  <td>
                    {vendor.rating ? (
                      <div className="rating">
                        {'⭐'.repeat(Math.round(vendor.rating))}
                        <span className="rating-value">({vendor.rating})</span>
                      </div>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(vendor.status)}`}>
                      {vendor.status}
                    </span>
                  </td>
                  <td>{new Date(vendor.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-chat"
                        onClick={() => setSelectedVendor(vendor)}
                        title="Chat with vendor"
                      >
                        <FiMessageCircle />
                      </button>
                      {vendor.status === 'Pending' && (
                        <>
                          <button
                            className="btn-approve"
                            onClick={() => updateStatus(vendor.id, 'Active')}
                          >
                            Approve
                          </button>
                          <button
                            className="btn-reject"
                            onClick={() => updateStatus(vendor.id, 'Inactive')}
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {vendor.status === 'Active' && (
                        <button
                          className="btn-suspend"
                          onClick={() => updateStatus(vendor.id, 'Suspended')}
                        >
                          Suspend
                        </button>
                      )}
                      {vendor.status === 'Suspended' && (
                        <button
                          className="btn-activate"
                          onClick={() => updateStatus(vendor.id, 'Active')}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Chat Modal - Only render when vendor is selected */}
      {selectedVendor && (
        <ChatButton
          vendor={selectedVendor}
          currentUser={currentUser || { id: 'ae9423d4-0894-46ba-b56d-921db40510da', first_name: 'Admin', last_name: 'User' }}
          token={token || localStorage.getItem('adminToken') || localStorage.getItem('token')}
          onClose={() => setSelectedVendor(null)}
        />
      )}
    </div>
  );
};

export default AdminVendors;


